import type { SpreadsheetTableDefinition } from '../protocol/spreadsheetEngineProtocol'

export type StructuredReferenceSuggestion = {
  name: string
  scopeLabel: string
  refersTo: string
}

const STRUCTURED_TABLE_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/

const normalizeIdentifier = (value: string | null | undefined): string => String(value ?? '').trim()

export const canUseStructuredReferenceTableName = (name: string | null | undefined): boolean => (
  STRUCTURED_TABLE_NAME_PATTERN.test(normalizeIdentifier(name))
)

export const canUseStructuredReferenceColumnName = (name: string | null | undefined): boolean => {
  const normalized = normalizeIdentifier(name)
  return normalized.length > 0 && !normalized.includes(']')
}

const addSuggestion = (
  suggestions: StructuredReferenceSuggestion[],
  seen: Set<string>,
  suggestion: StructuredReferenceSuggestion,
) => {
  const key = suggestion.name.toLowerCase()
  if (seen.has(key)) return
  seen.add(key)
  suggestions.push(suggestion)
}

export const buildStructuredReferenceSuggestions = (
  tables: Record<string, SpreadsheetTableDefinition> | null | undefined,
): StructuredReferenceSuggestion[] => {
  const suggestions: StructuredReferenceSuggestion[] = []
  const seen = new Set<string>()
  const seenTableNames = new Set<string>()
  const tableEntries = Object.values(tables ?? {})
    .map((table) => ({ table, name: normalizeIdentifier(table.name) }))
    .filter((entry) => canUseStructuredReferenceTableName(entry.name))
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))

  for (const { table, name: tableName } of tableEntries) {
    const tableKey = tableName.toLowerCase()
    if (seenTableNames.has(tableKey)) continue
    seenTableNames.add(tableKey)
    addSuggestion(suggestions, seen, {
      name: tableName,
      scopeLabel: 'Table',
      refersTo: `Data rows in ${tableName}`,
    })
    const hasHeaders = table.has_headers !== false
    const hasTotals = table.show_totals === true
    addSuggestion(suggestions, seen, {
      name: `${tableName}[#All]`,
      scopeLabel: 'Table',
      refersTo: `All rows in ${tableName}`,
    })
    addSuggestion(suggestions, seen, {
      name: `${tableName}[#Data]`,
      scopeLabel: 'Data',
      refersTo: `Data rows in ${tableName}`,
    })
    if (hasHeaders) {
      addSuggestion(suggestions, seen, {
        name: `${tableName}[#Headers]`,
        scopeLabel: 'Headers',
        refersTo: `Header row in ${tableName}`,
      })
    }
    if (hasTotals) {
      addSuggestion(suggestions, seen, {
        name: `${tableName}[#Totals]`,
        scopeLabel: 'Totals',
        refersTo: `Total row in ${tableName}`,
      })
    }

    const columns = (table.columns ?? [])
      .map((column) => normalizeIdentifier(column.name))
      .filter(canUseStructuredReferenceColumnName)
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    for (const columnName of columns) {
      addSuggestion(suggestions, seen, {
        name: `${tableName}[${columnName}]`,
        scopeLabel: 'Table Column',
        refersTo: `Data column ${columnName} in ${tableName}`,
      })
      addSuggestion(suggestions, seen, {
        name: `${tableName}[[#All],[${columnName}]]`,
        scopeLabel: 'Column All',
        refersTo: `All rows for ${columnName} in ${tableName}`,
      })
      if (hasHeaders) {
        addSuggestion(suggestions, seen, {
          name: `${tableName}[[#Headers],[${columnName}]]`,
          scopeLabel: 'Column Header',
          refersTo: `Header cell for ${columnName} in ${tableName}`,
        })
      }
      if (hasTotals) {
        addSuggestion(suggestions, seen, {
          name: `${tableName}[[#Totals],[${columnName}]]`,
          scopeLabel: 'Column Total',
          refersTo: `Total cell for ${columnName} in ${tableName}`,
        })
      }
    }
  }

  return suggestions
}
